/**
 * JWT utility functions for client-side token decoding
 */
import { logWarning, logError } from '../services/logging/log-handler.js';

/**
 * Decodes a JWT token and returns the payload
 * @param token The JWT token to decode
 * @returns The decoded payload or null if decoding fails
 */
export function decodeJWT(token: string): any | null {
  try {
    // All tokens now use the standard JWT format: header.payload.signature
    const parts = token.split(".");
    if (parts.length !== 3) {
      logWarning("Invalid JWT token format", "JWTUtils", undefined);
      return null;
    }

    const payload = parts[1];
    // Add padding if needed for base64url decoding
    const paddedPayload = payload + "=".repeat((4 - (payload.length % 4)) % 4);
    const decodedPayload = atob(paddedPayload.replace(/-/g, "+").replace(/_/g, "/"));
    return JSON.parse(decodedPayload);
  } catch (error) {
    logError(error, "JWTUtils", undefined, { message: "Failed to decode JWT token" });
    return null;
  }
}

/**
 * Extracts the expiration timestamp from a JWT token
 * @param token The JWT token
 * @returns The expiration timestamp (Unix epoch seconds) or null if not found
 */
export function getTokenExpiration(token: string): number | null {
  const decoded = decodeJWT(token);
  return decoded?.exp || null;
}

/**
 * Checks if a JWT token is expired
 * @param token The JWT token to check
 * @returns True if the token is expired, false otherwise
 */
export function isTokenExpired(token: string): boolean {
  const expiration = getTokenExpiration(token);
  if (!expiration) {
    return true; // If we can't get expiration, consider it expired
  }

  const currentTime = Math.floor(Date.now() / 1000);
  return expiration < currentTime;
}
